import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
} from "react"
import { LucideIcon } from "lucide-react"
import React from "react"
import Tooltip from "@mui/material/Tooltip"
import { styled } from "@mui/material"
import { TldrawUiIcon } from "tldraw"

export const selectedControlColor = "rgb(10, 108, 255)"

type Props = {
  children: ReactNode
  direction: "vertical" | "horizontal"
  padded?: boolean
  onPointerDown?: React.PointerEventHandler<HTMLDivElement> | undefined
  style?: CSSProperties
}

export const Toolbar = forwardRef(function Toolbar(
  { children, direction, padded, onPointerDown, style }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <ToolbarDiv
      ref={ref}
      direction={direction}
      padded={padded ?? true}
      onPointerDown={onPointerDown}
      style={style}
    >
      {children}
    </ToolbarDiv>
  )
})

type ToolbarDivProps = {
  direction: "vertical" | "horizontal"
  padded: boolean
}

const ToolbarDiv = styled("div")<ToolbarDivProps>({
  background: "white",
  display: "flex",
  boxShadow: "2px 2px 3px rgba(0, 0, 0, .2)",
  gap: 2,
  borderRadius: 4,
  // TODO(alvaro): i don't think this is necessary
  // overflow: "hidden",

  // TODO(alvaro): variants is a pigment-css feature, not mui
  variants: [
    {
      props: { direction: "horizontal" },
      style: {
        flexDirection: "row",
      },
    },
    {
      props: { direction: "vertical" },
      style: {
        flexDirection: "column",
      },
    },
    {
      props: { padded: true },
      style: {
        padding: 4,
      },
    },
  ],
})

type ToolbarIconButtonProps = {
  selected?: boolean
  icon: LucideIcon | string
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined
  tooltip?: string
  onHoverStart?: () => void
  onHoverEnd?: () => void
}
type ToolbarButtonProps = {
  selected?: boolean
  children: React.JSX.Element | null
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined
}

type LineProps = { color: string }
function Line({ color }: LineProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M22 2 2 22" />
    </svg>
  )
}

export const ToolbarIconButton = React.forwardRef(function ToolbarIconButton(
  {
    icon,
    selected,
    onClick,
    tooltip,
    onHoverStart,
    onHoverEnd,
    ...other
  }: ToolbarIconButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = icon

  return (
    <Tooltip title={tooltip}>
      <ToolbarIconButtonEl
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
        selected={selected}
        onClick={onClick}
        ref={forwardedRef}
        {...other}
      >
        {typeof icon === "string" ? (
          <Line color={selected ? "white" : "black"} />
        ) : (
          <Icon size={18} color={selected ? "white" : "black"} />
        )}
      </ToolbarIconButtonEl>
    </Tooltip>
  )
})

export const ToolbarButton = React.forwardRef(function ToolbarButton(
  { children, selected, onClick, ...other }: ToolbarButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <ToolbarIconButtonEl
      selected={selected}
      onClick={onClick}
      ref={forwardedRef}
      {...other}
    >
      {children}
    </ToolbarIconButtonEl>
  )
})

export const ToolbarSliverButton = forwardRef(function ToolbarSliverButton(
  { icon, selected, onClick }: ToolbarIconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = icon
  return (
    <ToolbarSliverButtonEl ref={ref} selected={selected} onClick={onClick}>
      <Icon
        size={18}
        color={selected ? "white" : "black"}
        style={{ pointerEvents: "none" }}
      />
    </ToolbarSliverButtonEl>
  )
})

type ToolbarIconButtonElProps = {
  selected?: boolean
}

const ToolbarIconButtonEl = styled("button")<ToolbarIconButtonElProps>({
  width: 40,
  height: 40,
  borderRadius: 4,
  flexGrow: 0,
  flexShrink: 0,
  background: "transparent",
  "&:hover": {
    background: "rgba(0, 0, 0, .05)",
  },
  "&:active": {
    background: "rgba(0, 0, 0, .1)",
  },
  variants: [
    {
      props: { selected: true },
      style: {
        background: selectedControlColor,
        "&:hover": {
          background: selectedControlColor,
        },
      },
    },
  ],
})

const ToolbarSliverButtonEl = styled("button")<ToolbarIconButtonElProps>({
  borderRadius: 4,
  flexShrink: 0,
  height: 24,
  background: "transparent",
  "&:hover": {
    background: "rgba(0, 0, 0, .05)",
  },
  "&:active": {
    background: "rgba(0, 0, 0, .1)",
  },
  variants: [
    {
      props: { selected: true },
      style: {
        background: selectedControlColor,
        "&:hover": {
          background: selectedControlColor,
        },
      },
    },
  ],
})
